import { unzip } from 'fflate';
import type { SourceTexture } from './atlas';
import { leafTones } from './palette';
import type { ResourcePackTextures } from '../render/structureAssets';

const MAX_PACK_BYTES = 256 * 1024 * 1024;
const MAX_TILE = 64;
const BLOCK_TEXTURE = /^assets\/minecraft\/textures\/block\/[a-z0-9_]+\.png$/;

/** ビューワー側の呼び名と、リソースパック内のファイル名の対応。 */
const packAliases: Record<string, string> = {
  water: 'water_still',
  lava: 'lava_still',
  grass_block: 'grass_block_top',
};

/** バニラではグレースケールで収録され、描画時に着色されるテクスチャ。 */
const tintRules: Array<[RegExp, number]> = [
  [/^grass_block_top$/, 0x7fb238],
  [/^(grass|short_grass|tall_grass|fern|large_fern)$/, 0x7fb238],
  [/^vine$/, 0x7fb238],
  [/^lily_pad$/, 0x71c35c],
  [/^water_still$/, 0x3f76e4],
];

export interface RawResourcePack {
  name: string;
  files: Map<string, Uint8Array>;
}

function tintFor(fileName: string): number | undefined {
  const leaf = /^(\w+?)_leaves$/.exec(fileName);
  if (leaf) return leafTones[leaf[1]] ?? leafTones.oak;
  return tintRules.find(([pattern]) => pattern.test(fileName))?.[1];
}

export function applyTint(pixels: Uint8ClampedArray, color: number) {
  const red = ((color >> 16) & 255) / 255;
  const green = ((color >> 8) & 255) / 255;
  const blue = (color & 255) / 255;
  for (let offset = 0; offset < pixels.length; offset += 4) {
    pixels[offset] *= red;
    pixels[offset + 1] *= green;
    pixels[offset + 2] *= blue;
  }
}

function overlay(base: Uint8ClampedArray, top: Uint8ClampedArray) {
  for (let offset = 0; offset < base.length && offset < top.length; offset += 4) {
    const alpha = top[offset + 3] / 255;
    if (alpha === 0) continue;
    base[offset] = base[offset] * (1 - alpha) + top[offset] * alpha;
    base[offset + 1] = base[offset + 1] * (1 - alpha) + top[offset + 1] * alpha;
    base[offset + 2] = base[offset + 2] * (1 - alpha) + top[offset + 2] * alpha;
  }
}

export async function decodeTexture(bytes: Uint8Array): Promise<SourceTexture | null> {
  const blob = new Blob([bytes.slice()], { type: 'image/png' });
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(blob);
  } catch {
    return null;
  }

  // アニメーションテクスチャは縦に並ぶので、先頭フレームだけを使う。
  const size = Math.min(bitmap.width, MAX_TILE);
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) {
    bitmap.close();
    return null;
  }
  context.imageSmoothingEnabled = false;
  context.drawImage(bitmap, 0, 0, bitmap.width, bitmap.width, 0, 0, size, size);
  bitmap.close();
  return { data: context.getImageData(0, 0, size, size).data, size };
}

/** .zip／.jarからブロックテクスチャのPNGだけを取り出す。展開のみでデコードはしない。 */
export async function readResourcePack(file: File): Promise<RawResourcePack> {
  if (file.size > MAX_PACK_BYTES) {
    throw new Error(`リソースパックは${MAX_PACK_BYTES / 1024 / 1024}MB以下にしてください。`);
  }

  const buffer = new Uint8Array(await file.arrayBuffer());
  const entries = await new Promise<Record<string, Uint8Array>>((resolve, reject) => {
    unzip(buffer, { filter: (entry) => BLOCK_TEXTURE.test(entry.name) }, (error, result) => {
      if (error) reject(new Error('zipを展開できませんでした。リソースパックか.jarを選んでください。'));
      else resolve(result);
    });
  });

  const files = new Map<string, Uint8Array>();
  Object.entries(entries).forEach(([path, data]) => {
    const name = path.slice(path.lastIndexOf('/') + 1, -4);
    if (data.length > 0) files.set(name, data);
  });

  if (files.size === 0) {
    throw new Error('assets/minecraft/textures/block にPNGが見つかりませんでした。');
  }
  return { name: file.name, files };
}

/**
 * 建築が実際に使うテクスチャだけをデコードする。パックに無いものは自前生成のままにする。
 */
export async function resolvePackTextures(pack: RawResourcePack, names: Iterable<string>): Promise<ResourcePackTextures> {
  const textures = new Map<string, SourceTexture>();
  let tile = 16;

  for (const name of new Set(names)) {
    const fileName = packAliases[name] ?? name;
    const bytes = pack.files.get(fileName);
    if (!bytes) continue;

    const decoded = await decodeTexture(bytes);
    if (!decoded) continue;

    const tint = tintFor(fileName);
    if (tint !== undefined) applyTint(decoded.data, tint);

    if (fileName === 'grass_block_side') {
      const overlayBytes = pack.files.get('grass_block_side_overlay');
      const decodedOverlay = overlayBytes ? await decodeTexture(overlayBytes) : null;
      if (decodedOverlay && decodedOverlay.size === decoded.size) {
        applyTint(decodedOverlay.data, 0x7fb238);
        overlay(decoded.data, decodedOverlay.data);
      }
    }

    textures.set(name, decoded);
    tile = Math.max(tile, decoded.size);
  }

  if (textures.size === 0) {
    throw new Error('この建築で使うブロックのテクスチャがパック内に見つかりませんでした。');
  }
  return { name: pack.name, tile: Math.min(MAX_TILE, tile), textures };
}
